import { useState, useEffect, useRef, useCallback } from 'react';
import { api, mintSocketTicket } from '../api/client';

export interface ChatMessage {
  id: string;
  jobId: string;
  userId: string;
  message: string | null;
  messageType: string;
  replyToId: string | null;
  editedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
    initials: string;
  };
}

interface UseJobChatReturn {
  messages: ChatMessage[];
  sendMessage: (text: string, replyToId?: string) => Promise<void>;
  editMessage: (id: string, text: string) => Promise<void>;
  deleteMessage: (id: string) => Promise<void>;
  loadMore: () => Promise<void>;
  isConnected: boolean;
  isLoading: boolean;
  hasMore: boolean;
}

export function useJobChat(jobId: string | null, onNewMessage?: (msg: ChatMessage) => void): UseJobChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const prevCountRef = useRef(0);

  // Poll for new messages (every 5s when chat is open)
  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(() => {
      api.chat.getMessages(jobId)
        .then(res => {
          const data = res.data;
          const newMsgs: ChatMessage[] = data.data || [];
          setMessages(prev => {
            // Notify about new messages from others
            if (newMsgs.length > prevCountRef.current && prevCountRef.current > 0) {
              const latest = newMsgs[newMsgs.length - 1];
              if (latest && onNewMessage) onNewMessage(latest);
            }
            prevCountRef.current = newMsgs.length;
            if (newMsgs.length === prev.length) return prev;
            return newMsgs;
          });
          setIsConnected(true); // mark as connected when polling works
        })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [jobId, onNewMessage]);

  // Connect WebSocket
  //
  // Authenticated with a 60-second ticket from `mintSocketTicket()`, not the
  // session JWT. The browser WebSocket API cannot set headers, so the credential
  // has to travel in the query string — but a query string is logged, so what
  // travels there is now worthless a minute after it is issued rather than being
  // a 7-day key to the whole API. See `mintSocketTicket` for the full trade.
  //
  // The mint is async, so the socket is created after an await and the cleanup
  // has to cope with unmounting while that promise is still in flight — hence
  // `cancelled` and the mutable `ws`.
  useEffect(() => {
    if (!jobId) return;

    let ws: WebSocket | null = null;
    let cancelled = false;

    mintSocketTicket().then(ticket => {
      if (cancelled) return;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const apiUrl = process.env.REACT_APP_API_URL || '';
      let wsHost: string;

      if (apiUrl.startsWith('http')) {
        wsHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api\/v1$/, '');
      } else {
        wsHost = window.location.host;
      }

      // Do not log this URL.
      const wsUrl = `${protocol}//${wsHost}/ws?ticket=${encodeURIComponent(ticket)}`;

      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        ws?.send(JSON.stringify({ type: 'join_job_chat', jobId }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'job_chat_message') {
            setMessages(prev => [...prev, data.data]);
          }

          if (data.type === 'job_chat_edit') {
            setMessages(prev =>
              prev.map(m =>
                m.id === data.data.id
                  ? { ...m, message: data.data.message, editedAt: data.data.editedAt }
                  : m
              )
            );
          }

          if (data.type === 'job_chat_delete') {
            setMessages(prev =>
              prev.map(m =>
                m.id === data.data.id ? { ...m, message: null, deletedAt: new Date().toISOString() } : m
              )
            );
          }
        } catch {}
      };

      ws.onclose = () => setIsConnected(false);
      ws.onerror = () => setIsConnected(false);
    }).catch(() => {
      // Ticket mint failed (offline, 401, 5xx) — the 5s poll above still
      // carries the chat, which is why there is no reconnect here.
    });

    return () => {
      cancelled = true;
      if (ws) {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'leave_job_chat', jobId }));
        }
        ws.close();
      }
      wsRef.current = null;
    };
  }, [jobId]);

  // Load initial messages
  useEffect(() => {
    if (!jobId) return;

    setIsLoading(true);
    api.chat.getMessages(jobId)
      .then(res => {
        const data = res.data;
        const msgs = data.data || [];
        setMessages(msgs);
        prevCountRef.current = msgs.length;
        setCursor(data.nextCursor || null);
        setHasMore(!!data.nextCursor);
        setIsConnected(true);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [jobId]);

  const loadMore = useCallback(async () => {
    if (!jobId || !cursor || !hasMore) return;
    try {
      const res = await api.chat.getMessages(jobId, cursor);
      const data = res.data;
      setMessages(prev => [...(data.data || []), ...prev]);
      setCursor(data.nextCursor || null);
      setHasMore(!!data.nextCursor);
    } catch (err) {
      console.error('loadMore error:', err);
    }
  }, [jobId, cursor, hasMore]);

  const sendMessage = useCallback(async (text: string, replyToId?: string) => {
    if (!jobId) return;
    const res = await api.chat.sendMessage(jobId, text, replyToId);
    // Add own message immediately (server also broadcasts but we add optimistically)
    if (res.data?.data) {
      setMessages(prev => {
        // Avoid duplicate if WS already delivered it
        if (prev.some(m => m.id === res.data.data.id)) return prev;
        return [...prev, res.data.data];
      });
    }
  }, [jobId]);

  const editMessage = useCallback(async (id: string, text: string) => {
    await api.chat.editMessage(id, text);
    setMessages(prev =>
      prev.map(m =>
        m.id === id ? { ...m, message: text, editedAt: new Date().toISOString() } : m
      )
    );
  }, []);

  const deleteMessage = useCallback(async (id: string) => {
    await api.chat.deleteMessage(id);
    setMessages(prev =>
      prev.map(m =>
        m.id === id ? { ...m, message: null, deletedAt: new Date().toISOString() } : m
      )
    );
  }, []);

  return {
    messages,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMore,
    isConnected,
    isLoading,
    hasMore,
  };
}
